import { Chip, type ChipStatus } from '@appcharge/shared-ui';
import { CellContext, type ColumnDef, type Row } from '@tanstack/react-table';
import { type FormikProps, ScheduleTimeFrame } from 'common/contracts';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import {
    EAppearanceStatus,
    EPendingAppearanceStatus
} from 'constants/enums';
import dayjs from 'dayjs';
import { statusUtils } from 'utils/scheduleUtils';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';

interface ScheduleAppearancesTableProps {
    formikProps: FormikProps;
    appearancesTableData: ScheduleTimeFrame[];
    onEdit: (startTime: Date | string, endTime: Date | string, notes: string) => void;
    onDelete: () => void;
}

const statusMapping: Record<string, ChipStatus> = {
    [EAppearanceStatus.RUNNING]: 'success',
    [EAppearanceStatus.UPCOMING]: 'warning',
    [EAppearanceStatus.ENDED]: 'secondary',
    [EPendingAppearanceStatus.PENDING_SAVE]: 'secondary'
};

const ScheduleAppearancesTable: React.FC<ScheduleAppearancesTableProps> = ({
    formikProps,
    appearancesTableData,
    onEdit,
    onDelete
}) => {
    const { values, setFieldValue } = formikProps;

    const removeTimeframe = (startTime: string, endTime: string) => {
        const newTimeFrames = values?.schedule?.timeFrames?.filter(
            (timeFrame: ScheduleTimeFrame) => {
                const startMatches = new Date(timeFrame.startTime).getTime() === new Date(startTime).getTime();
                const endMatches = new Date(timeFrame.endTime).getTime() === new Date(endTime).getTime();
                return !(startMatches && endMatches);
            }
        );

        setFieldValue('schedule', {
            permanent: !newTimeFrames.length
                ? false
                : values?.schedule.permanent,
            timeFrames: newTimeFrames
        });
        onDelete();
    };

    const columns: ColumnDef<ScheduleTimeFrame>[] = [
        {
            accessorKey: 'startTime',
            header: 'Start',
            size: 150,
            cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
                dayjs(cell.getValue() as string)
                    .utc()
                    .format(DATE_TIME_FORMAT_SHORTER)
        },
        {
            accessorKey: 'endTime',
            header: 'End',
            size: 150,
            cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
                dayjs(cell.getValue() as string)
                    .utc()
                    .format(DATE_TIME_FORMAT_SHORTER)
        },
        {
            accessorKey: 'notes',
            header: 'Notes',
            size: 150,
            cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
                cell.getValue() as string
        },
        {
            accessorKey: 'status',
            header: 'Status',
            size: 200,
            cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => {
                const { startTime, endTime, id } = row.original;
                const pendingSaveStatus = id?.startsWith('id-');
                const status = pendingSaveStatus
                    ? EPendingAppearanceStatus.PENDING_SAVE
                    : statusUtils.determineStatus(new Date(startTime), new Date(endTime));

                return (
                    <Chip variant="flat" status={statusMapping[status]}>
                        {status}
                    </Chip>
                );
            }
        },
        {
            accessorKey: 'actions',
            header: '',
            size: 80,
            cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => (
                <ActionCell
                    rowData={row.original}
                    actions={[
                        {
                            label: 'Edit',
                            handler: () => {
                                onEdit(
                                    row.original.startTime,
                                    row.original.endTime,
                                    row.original.notes || ''
                                );
                            }
                        },
                        {
                            label: 'Delete',
                            handler: () =>
                                removeTimeframe(
                                    String(row.original.startTime),
                                    String(row.original.endTime)
                                )
                        }
                    ]}
                />
            )
        }
    ];

    const tableConfig = {
        data: appearancesTableData,
        showPagination: false,
        columns,
        enableColumnResizing: true
    };

    return (
        <div className="mt-6">
            <DataTable {...tableConfig} />
        </div>
    );
};

export default ScheduleAppearancesTable;

